import React, { useMemo, useState } from 'react';
import Svg, { Polygon } from 'react-native-svg';
import { ImageMapArea } from './attachment-list-types';
import { ImageWithLoading } from '@/components/image-with-loading';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { useTheme } from '@/contexts/theme-context';

interface Props {
  containerStyle?: StyleProp<ViewStyle>;
  imageMapAreas: ImageMapArea[];
  imageURL: string;
  onAreaSelect: (area: ImageMapArea) => void;
  selectedAreaID?: string;
  width?: number;
  zoomLevel?: number;
}

export const AttachmentListImage: React.FC<Props> = ({
  containerStyle,
  width,
  imageMapAreas,
  imageURL,
  onAreaSelect,
  selectedAreaID,
  zoomLevel = 1,
}) => {
  const theme = useTheme();
  const [imageDimensions, setImageDimensions] = useState<{
    height: number;
    width: number;
  }>({ height: 100, width: 100 });

  const aspectRatio = useMemo(() => {
    if (typeof width !== 'number') {
      return 1;
    }
    return width / imageDimensions.width;
  }, [width, imageDimensions]);

  const minHeight = useMemo(() => {
    if (!imageDimensions || typeof width !== 'number') {
      return undefined;
    }
    return imageDimensions.height * aspectRatio;
  }, [aspectRatio, imageDimensions, width]);

  return (
    <View
      style={[
        StyleSheet.absoluteFill,
        styles.imageContainer,
        minHeight ? { height: minHeight * zoomLevel } : null,
        containerStyle,
      ]}
    >
      <ImageWithLoading
        alt=""
        contentFit="contain"
        onLoad={(e) => {
          // Only calculate aspect ratio and min height if width is provided
          setImageDimensions({
            height: e.source.height,
            width: e.source.width,
          });
        }}
        source={{ uri: imageURL }}
        style={[
          styles.image,
          minHeight ? { height: minHeight * zoomLevel } : null,
        ]}
        testID="image-map-image"
      />
      {imageMapAreas.length > 0 ? (
        <Svg
          height={minHeight ? minHeight * zoomLevel : '100%'}
          pointerEvents="box-none"
          style={styles.areaContainer}
          viewBox={`0 0 ${imageDimensions.width * aspectRatio} ${imageDimensions.height * aspectRatio}`}
          width="100%"
        >
          {imageMapAreas.map((area, idx) => (
            <Polygon
              // TODO - replace with SK
              fill={
                area.id === selectedAreaID
                  ? theme.colors.positive
                  : theme.colors.pepperGreen
              }
              key={`${imageURL}-${area.id}-${idx}`}
              onPress={() => {
                onAreaSelect(area);
              }}
              opacity={area.id === selectedAreaID ? 0.6 : 0.4}
              points={area.coordinates
                .map(
                  (point) =>
                    `${point[0] * aspectRatio},${point[1] * aspectRatio}`,
                )
                .join(' ')}
              stroke="transparent"
              // @ts-ignore style is not defined on Polygon
              style={styles.area}
              testID={`image-map-area-${area.id}`}
            />
          ))}
        </Svg>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  area: {
    cursor: 'pointer',
  },
  areaContainer: {
    position: 'absolute',
  },
  image: {
    height: '100%',
    pointerEvents: 'none',
    width: '100%',
  },
  imageContainer: {
    width: '100%',
  },
});
